import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, queryString } from '../api';
import { useAuth } from '../auth';
import {
  EmptyState,
  ErrorNotice,
  Field,
  Notice,
  SelectField,
  Spinner,
  formatDate,
  formatMoney,
} from '../components/ui';
import type {
  Payment,
  Subscription,
  SubscriptionRow,
  SubscriptionStatus,
  SubscriptionSummary,
} from '../types';

/**
 * The treasurer's tick-off screen.
 *
 * It opens on what is still outstanding rather than on everything, because
 * "who hasn't paid" is the entire job this screen exists for — a list of 150
 * people that happens to include the 12 who owe money is not the same tool.
 *
 * Recording a payment happens in the row itself. Going to a separate page to
 * type one number and come back is how a volunteer ends up back in the
 * spreadsheet.
 */
export function SubscriptionsPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const outstandingOnly = params.get('outstanding') !== '0';

  const [draft, setDraft] = useState(query);
  const [rows, setRows] = useState<SubscriptionRow[] | null>(null);
  const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
  const [open, setOpen] = useState(true);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const firstRender = useRef(true);

  useEffect(() => setDraft(query), [query]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (draft === query) return;
    const timer = window.setTimeout(() => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (draft === '') next.delete('q');
          else next.set('q', draft);
          return next;
        },
        { replace: true },
      );
    }, 250);
    return () => window.clearTimeout(timer);
  }, [draft, query, setParams]);

  function load() {
    setLoading(true);
    setError(null);
    api
      .get<{
        open: boolean;
        rows: SubscriptionRow[];
        total: number;
        summary?: SubscriptionSummary;
      }>(`/subscriptions${queryString({ q: query, outstanding: outstandingOnly ? '1' : '0' })}`)
      .then((result) => {
        setOpen(result.open);
        setRows(result.rows);
        setTotal(result.total);
        if (result.summary) setSummary(result.summary);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }

  useEffect(load, [query, outstandingOnly]);

  function announce(message: string) {
    setFlash(message);
    window.setTimeout(() => setFlash(null), 4000);
  }

  if (!open && !loading) {
    return (
      <>
        <div className="page-head">
          <h1>Subscriptions</h1>
        </div>
        <Notice kind="info">
          No membership year is current yet. Set one up under{' '}
          <Link to="/rates">Rates &amp; settings</Link> before taking payments.
        </Notice>
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <h1>Subscriptions</h1>
        <div className="head-actions">
          <span className="muted">{loading && rows === null ? 'Loading…' : `${total} shown`}</span>
          {can('payments_record') ? (
            <Link to="/subscriptions/import" className="button-link">
              Import a bank statement
            </Link>
          ) : null}
          {can('subscriptions_view') ? (
            <Link to="/reports/agm" className="button-link">
              AGM report
            </Link>
          ) : null}
          {can('subscriptions_view') ? (
            <Link to="/returns" className="button-link">
              BDI eR2 return
            </Link>
          ) : null}
        </div>
      </div>

      {flash ? <Notice kind="success">{flash}</Notice> : null}
      <ErrorNotice error={error} />

      {summary !== null ? (
        <div className="tiles">
          <div className="tile">
            <span className="tile-label">Still owed</span>
            <span className="tile-value">
              {formatMoney(Math.max(0, summary.total_due - summary.total_received))}
            </span>
            <span className="muted small">of {formatMoney(summary.total_due)} due</span>
          </div>
          <div className="tile">
            <span className="tile-label">Received</span>
            <span className="tile-value">{formatMoney(summary.total_received)}</span>
            <span className="muted small">{summary.paid_count} paid in full</span>
          </div>
          <div className="tile">
            <span className="tile-label">Not settled</span>
            <span className="tile-value">{summary.awaiting_count + summary.part_paid_count}</span>
            <span className="muted small">
              {summary.awaiting_count} not started, {summary.part_paid_count} part paid
            </span>
          </div>
          {summary.bdi_referral_count > 0 ? (
            <div className="tile tile-flag">
              <span className="tile-label">BDI referrals</span>
              <span className="tile-value">{summary.bdi_referral_count}</span>
              <span className="muted small">over the band table, need contacting</span>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="filters">
        <div className="field search">
          <label htmlFor="subscription-search">Search</label>
          <input
            id="subscription-search"
            type="search"
            placeholder="Name or email"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
        </div>
        <div className="chips" role="group" aria-label="Which subscriptions to show">
          <button
            type="button"
            className={outstandingOnly ? 'chip chip-on' : 'chip'}
            aria-pressed={outstandingOnly}
            onClick={() =>
              setParams((previous) => {
                const next = new URLSearchParams(previous);
                next.delete('outstanding');
                return next;
              })
            }
          >
            Still owing
          </button>
          <button
            type="button"
            className={!outstandingOnly ? 'chip chip-on' : 'chip'}
            aria-pressed={!outstandingOnly}
            onClick={() =>
              setParams((previous) => {
                const next = new URLSearchParams(previous);
                next.set('outstanding', '0');
                return next;
              })
            }
          >
            Everyone
          </button>
        </div>
      </div>

      {loading && rows === null ? <Spinner label="Loading subscriptions" /> : null}

      {rows !== null && rows.length === 0 ? (
        <EmptyState title={outstandingOnly ? 'Everyone has paid' : 'No subscriptions match'}>
          {outstandingOnly ? 'Nothing outstanding for this year.' : 'Try a different search.'}
        </EmptyState>
      ) : null}

      {rows !== null && rows.length > 0 ? (
        <table className="members">
          <thead>
            <tr>
              <th scope="col">Member</th>
              <th scope="col">Category</th>
              <th scope="col">Due</th>
              <th scope="col">Paid</th>
              <th scope="col">Status</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <SubscriptionRowView
                key={row.id}
                row={row}
                canRecord={can('payments_record')}
                expanded={expanded === row.id}
                onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
                onChanged={(message) => {
                  announce(message);
                  load();
                }}
                onError={setError}
              />
            ))}
          </tbody>
        </table>
      ) : null}
    </>
  );
}

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  awaiting_payment: 'not paid',
  part_paid: 'part paid',
  paid: 'paid',
  waived: 'waived',
  cancelled: 'cancelled',
};

function SubscriptionRowView({
  row,
  canRecord,
  expanded,
  onToggle,
  onChanged,
  onError,
}: {
  row: SubscriptionRow;
  canRecord: boolean;
  expanded: boolean;
  onToggle: () => void;
  onChanged: (message: string) => void;
  onError: (error: unknown) => void;
}) {
  return (
    <>
      <tr className={expanded ? 'row-open' : ''}>
        <td data-label="Member">
          <Link to={`/members/${row.member_id}`} className="member-name">
            {[row.title, row.first_name, row.last_name].filter(Boolean).join(' ')}
          </Link>
          {row.needs_bdi_referral ? (
            <span className="badges">
              <span className="badge" title={`${row.colony_count} colonies declared`}>
                BDI referral
              </span>
            </span>
          ) : null}
        </td>
        <td data-label="Category">{row.category_name}</td>
        <td data-label="Due" className="numeric">
          {formatMoney(row.total_due)}
        </td>
        <td data-label="Paid" className="numeric">
          {formatMoney(row.paid_total)}
          {row.outstanding > 0 ? (
            <span className="muted block">{formatMoney(row.outstanding)} left</span>
          ) : null}
        </td>
        <td data-label="Status">
          <span className={`pill pill-sub-${row.status}`}>{STATUS_LABELS[row.status]}</span>
        </td>
        <td data-label="">
          <button type="button" className="link" onClick={onToggle}>
            {expanded ? 'Close' : canRecord ? 'Record payment' : 'Details'}
          </button>
        </td>
      </tr>
      {expanded ? (
        <tr className="row-detail">
          <td colSpan={6}>
            <SubscriptionDetail
              subscriptionId={row.id}
              canRecord={canRecord}
              onChanged={onChanged}
              onError={onError}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function SubscriptionDetail({
  subscriptionId,
  canRecord,
  onChanged,
  onError,
}: {
  subscriptionId: number;
  canRecord: boolean;
  onChanged: (message: string) => void;
  onError: (error: unknown) => void;
}) {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [error, setError] = useState<unknown>(null);

  function load() {
    api
      .get<{ subscription: Subscription }>(`/subscriptions/${subscriptionId}`)
      .then((result) => setSubscription(result.subscription))
      .catch(setError);
  }

  useEffect(load, [subscriptionId]);

  if (error !== null) return <ErrorNotice error={error} />;
  if (subscription === null) return <Spinner label="Loading" />;

  return (
    <div className="subscription-detail">
      <div className="subscription-breakdown">
        <div>
          <span className="muted small">Membership</span>
          <span>{formatMoney(subscription.category_amount)}</span>
        </div>
        {subscription.carries_bdi ? (
          <div>
            <span className="muted small">
              BDI{subscription.colony_count !== null ? ` (${subscription.colony_count} colonies)` : ''}
            </span>
            <span>
              {subscription.needs_bdi_referral ? 'referral' : formatMoney(subscription.bdi_amount)}
            </span>
          </div>
        ) : null}
        <div>
          <span className="muted small">Total due</span>
          <span>
            <strong>{formatMoney(subscription.total_due)}</strong>
          </span>
        </div>
        {subscription.bdi_cover_starts_on ? (
          <div>
            <span className="muted small">BDI cover starts</span>
            <span>{formatDate(subscription.bdi_cover_starts_on)}</span>
          </div>
        ) : null}
      </div>

      {subscription.needs_bdi_referral ? (
        <Notice kind="info">
          {subscription.colony_count} colonies declared — over the band table, so BDI cover has to be
          arranged with them directly.
        </Notice>
      ) : null}

      <h3>Payments</h3>
      {subscription.payments.length === 0 ? (
        <p className="muted small">Nothing recorded yet.</p>
      ) : (
        <ul className="payment-list">
          {subscription.payments.map((payment) => (
            <PaymentRow
              key={payment.id}
              payment={payment}
              canRecord={canRecord}
              onRemoved={() => {
                onChanged('Payment removed.');
                load();
              }}
              onError={onError}
            />
          ))}
        </ul>
      )}

      {canRecord ? (
        <RecordPaymentForm
          subscription={subscription}
          onRecorded={() => {
            onChanged('Payment recorded.');
            load();
          }}
        />
      ) : null}

      {canRecord ? (
        <ManualStatusControls
          subscription={subscription}
          onChanged={(message) => {
            onChanged(message);
            load();
          }}
          onError={onError}
        />
      ) : null}
    </div>
  );
}

function PaymentRow({
  payment,
  canRecord,
  onRemoved,
  onError,
}: {
  payment: Payment;
  canRecord: boolean;
  onRemoved: () => void;
  onError: (error: unknown) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  async function remove() {
    setBusy(true);
    try {
      await api.del(`/payments/${payment.id}`);
      onRemoved();
    } catch (failure) {
      onError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li>
      <span className="payment-amount">{formatMoney(payment.amount)}</span>
      <span className="muted">
        {payment.method} · {formatDate(payment.received_on)}
        {payment.reference ? ` · ${payment.reference}` : ''}
        {payment.recorded_by_name ? ` · entered by ${payment.recorded_by_name}` : ''}
      </span>
      {canRecord ? (
        confirming ? (
          <span className="payment-confirm">
            <button type="button" className="link danger" disabled={busy} onClick={() => void remove()}>
              {busy ? 'Removing…' : 'Yes, remove it'}
            </button>
            <button type="button" className="link" onClick={() => setConfirming(false)}>
              Keep
            </button>
          </span>
        ) : (
          <button type="button" className="link" onClick={() => setConfirming(true)}>
            Remove
          </button>
        )
      ) : null}
    </li>
  );
}

function RecordPaymentForm({
  subscription,
  onRecorded,
}: {
  subscription: Subscription;
  onRecorded: () => void;
}) {
  const settled = subscription.status === 'waived' || subscription.status === 'cancelled';
  const [amount, setAmount] = useState(
    subscription.outstanding > 0 ? subscription.outstanding.toFixed(2) : '',
  );
  const [method, setMethod] = useState('bacs');
  const [receivedOn, setReceivedOn] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState(subscription.last_name.toUpperCase());
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (settled) return null;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post(`/subscriptions/${subscription.id}/payments`, {
        amount: Number(amount),
        method,
        received_on: receivedOn,
        reference: reference || null,
      });
      onRecorded();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form record-payment" onSubmit={onSubmit}>
      <h3>Record a payment</h3>
      <ErrorNotice error={error} />
      <div className="grid">
        <Field
          label="Amount"
          type="number"
          min={0.01}
          step={0.01}
          required
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          hint={subscription.outstanding > 0 ? `${formatMoney(subscription.outstanding)} outstanding` : undefined}
        />
        <SelectField label="How" value={method} onChange={(e) => setMethod(e.target.value)}>
          <option value="bacs">Bank transfer</option>
          <option value="cheque">Cheque</option>
          <option value="cash">Cash</option>
          <option value="card">Card</option>
        </SelectField>
        <Field
          label="Date received"
          type="date"
          required
          value={receivedOn}
          onChange={(e) => setReceivedOn(e.target.value)}
        />
        <Field
          label="Reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          hint="Whatever appeared on the bank statement."
        />
      </div>
      <div className="actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Recording…' : 'Record payment'}
        </button>
      </div>
    </form>
  );
}

/**
 * Waive and cancel only. paid and part paid are not offered because they are
 * not decisions — they follow from the payments above, and the API refuses
 * them outright.
 */
function ManualStatusControls({
  subscription,
  onChanged,
  onError,
}: {
  subscription: Subscription;
  onChanged: (message: string) => void;
  onError: (error: unknown) => void;
}) {
  const [busy, setBusy] = useState(false);
  const overridden = subscription.status === 'waived' || subscription.status === 'cancelled';

  async function set(status: string, message: string) {
    setBusy(true);
    try {
      await api.put(`/subscriptions/${subscription.id}/status`, { status });
      onChanged(message);
    } catch (failure) {
      onError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="manual-status">
      {overridden ? (
        <>
          <span className="muted small">
            Marked {subscription.status} by hand, so the payments above are not deciding this.
          </span>
          <button type="button" className="link" disabled={busy} onClick={() => void set('reinstate', 'Reinstated.')}>
            Reinstate it
          </button>
        </>
      ) : (
        <>
          <span className="muted small">Not collecting this one?</span>
          <button type="button" className="link" disabled={busy} onClick={() => void set('waived', 'Subscription waived.')}>
            Waive the fee
          </button>
          <button type="button" className="link" disabled={busy} onClick={() => void set('cancelled', 'Subscription cancelled.')}>
            Cancel it
          </button>
        </>
      )}
    </div>
  );
}
